import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft, Sparkles, Search, Filter, RefreshCw, Check, X, Clock,
  CreditCard, AlertCircle, CheckCircle, Eye, Calendar, Building2,
  DollarSign, Users, TrendingUp, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
const statusConfig = {
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700', icon: Clock },
  pending_payment: { label: 'Pending Payment', color: 'bg-blue-100 text-blue-700', icon: CreditCard },
  confirmed: { label: 'Confirmed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  active: { label: 'Active', color: 'bg-purple-100 text-purple-700', icon: Sparkles },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: X }
};

const tierConfig = {
  standard: { label: 'Standard', color: 'bg-cyan-100 text-cyan-700' },
  premium: { label: 'Premium', color: 'bg-purple-100 text-purple-700' },
  luxury: { label: 'Luxury', color: 'bg-amber-100 text-amber-700' },
  custom: { label: 'Custom', color: 'bg-emerald-100 text-emerald-700' }
};

const AdminAIBookings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    revenue: 0
  });
  const [filters, setFilters] = useState({
    status: 'all',
    tier: 'all',
    search: ''
  });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.tier !== 'all') params.append('tier', filters.tier);
      params.append('limit', '100');

      const [bookingsRes, pendingRes] = await Promise.all([
        api.get(`/admin/booking/list?${params.toString()}`),
        api.get('/admin/booking/pending-approval')
      ]);

      setBookings(bookingsRes.data.bookings || []);
      setPendingApprovals(pendingRes.data || []);

      // Calculate stats
      const allBookings = bookingsRes.data.bookings || [];
      setStats({
        total: bookingsRes.data.total || allBookings.length,
        pending: pendingRes.data?.length || 0,
        confirmed: allBookings.filter(b => b.status === 'confirmed').length,
        revenue: allBookings.reduce((sum, b) => 
          b.payment_status === 'paid' ? sum + (b.total_price || 0) : sum, 0
        )
      });
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleApproveBooking = async (bookingId, action) => {
    setProcessing(true);
    try {
      const status = action === 'approve' ? 'pending_payment' : 'cancelled';
      await api.patch(`/admin/booking/${bookingId}/status`, {
        status,
        admin_notes: adminNotes || null
      });

      toast.success(action === 'approve' ? 'Booking approved! Client can now proceed to payment.' : 'Booking rejected.');
      setApprovalDialog(false);
      setSelectedBooking(null);
      setAdminNotes('');
      fetchBookings();
    } catch (error) {
      console.error('Failed to update booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmBooking = async (bookingId) => {
    setProcessing(true);
    try {
      await api.patch(`/admin/booking/${bookingId}/status`, {
        status: 'confirmed',
        admin_notes: 'Manually confirmed by admin'
      });

      toast.success('Booking confirmed successfully!');
      fetchBookings();
    } catch (error) {
      console.error('Failed to confirm booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm booking');
    } finally {
      setProcessing(false);
    }
  };

  const openApprovalDialog = (booking) => {
    setSelectedBooking(booking);
    setAdminNotes('');
    setApprovalDialog(true);
  };

  const filteredBookings = bookings.filter(booking => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        booking.booking_number?.toLowerCase().includes(search) ||
        booking.client?.email?.toLowerCase().includes(search) ||
        booking.ambassador?.name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-ai-bookings">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin/home')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-purple-500" />
                  AI Ambassador Bookings
                </h1>
                <p className="text-muted-foreground">Manage and approve AI Ambassador bookings</p>
              </div>
            </div>
            <Button onClick={fetchBookings} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Bookings</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Approval</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Confirmed</p>
                <p className="text-2xl font-bold">{stats.confirmed}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Revenue (Paid)</p>
                <p className="text-2xl font-bold">${stats.revenue.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white">
            <TabsTrigger value="all" className="data-[state=active]:bg-purple-100">
              All Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="approval" className="data-[state=active]:bg-amber-100">
              <AlertCircle className="w-4 h-4 mr-1" />
              Needs Approval ({pendingApprovals.length})
            </TabsTrigger>
          </TabsList>

          {/* Filters */}
          <Card className="bg-white mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by booking #, client, ambassador..."
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending_approval">Pending Approval</SelectItem>
                    <SelectItem value="pending_payment">Pending Payment</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filters.tier}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, tier: value }))}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Filter by tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="luxury">Luxury</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* All Bookings Tab */}
          <TabsContent value="all">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>All Bookings</CardTitle>
                <CardDescription>View and manage all AI Ambassador bookings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="bookings-table">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Booking</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Client</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Ambassador</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Tier</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Total</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Payment</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBookings.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="p-8 text-center text-muted-foreground">
                            No bookings found
                          </td>
                        </tr>
                      ) : (
                        filteredBookings.map((booking) => {
                          const status = statusConfig[booking.status] || statusConfig.pending_approval;
                          const tier = tierConfig[booking.tier] || tierConfig.standard;
                          const StatusIcon = status.icon;

                          return (
                            <tr key={booking.id} className="border-b hover:bg-slate-50">
                              <td className="p-3">
                                <p className="font-medium">{booking.booking_number}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatDateMonthDay(booking.created_at)}
                                </p>
                              </td>
                              <td className="p-3">
                                <p className="font-medium">{booking.client?.name || 'N/A'}</p>
                                <p className="text-xs text-muted-foreground">{booking.client?.email}</p>
                              </td>
                              <td className="p-3">
                                <p className="font-medium">{booking.ambassador?.name}</p>
                              </td>
                              <td className="p-3">
                                <Badge className={tier.color}>{tier.label}</Badge>
                              </td>
                              <td className="p-3 font-medium">
                                ${booking.total_price?.toLocaleString()}
                              </td>
                              <td className="p-3">
                                <Badge className={status.color}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {status.label}
                                </Badge>
                              </td>
                              <td className="p-3">
                                <Badge className={booking.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                                  {booking.payment_status || 'unpaid'}
                                </Badge>
                              </td>
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate(`/admin/ambassador-booking/${booking.id}`)}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  {booking.status === 'pending_approval' && (
                                    <Button
                                      size="sm"
                                      className="bg-green-500 hover:bg-green-600"
                                      onClick={() => openApprovalDialog(booking)}
                                    >
                                      Review
                                    </Button>
                                  )}
                                  {booking.status === 'pending_payment' && booking.requires_approval && (
                                    <Button
                                      size="sm"
                                      className="bg-purple-500 hover:bg-purple-600"
                                      onClick={() => handleConfirmBooking(booking.id)}
                                      disabled={processing}
                                    >
                                      Confirm
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Approval Queue Tab */}
          <TabsContent value="approval">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Approval Queue
                </CardTitle>
                <CardDescription>
                  Luxury and Custom tier bookings require admin approval before payment
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingApprovals.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                    <p className="font-medium">All caught up!</p>
                    <p className="text-sm">No bookings pending approval</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingApprovals.map((booking) => {
                      const tier = tierConfig[booking.tier] || tierConfig.custom;

                      return (
                        <Card key={booking.id} className="border-amber-200 bg-amber-50/50">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-4">
                                <div className="p-2 bg-amber-100 rounded-lg">
                                  <Clock className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="font-semibold">{booking.booking_number}</p>
                                    <Badge className={tier.color}>{tier.label}</Badge>
                                  </div>
                                  <p className="text-sm">
                                    <span className="text-muted-foreground">Client:</span>{' '}
                                    {booking.client?.name || booking.client?.email}
                                  </p>
                                  <p className="text-sm">
                                    <span className="text-muted-foreground">Ambassador:</span>{' '}
                                    {booking.ambassador_name}
                                  </p>
                                  <p className="text-sm">
                                    <span className="text-muted-foreground">Scope:</span>{' '}
                                    {booking.usage_scope?.replace(/_/g, ' ')} • {booking.deployment_type?.replace(/_/g, ' ')}
                                  </p>
                                  {booking.start_date && (
                                    <p className="text-sm flex items-center gap-1 mt-1">
                                      <Calendar className="w-3 h-3" />
                                      {booking.start_date}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold">${booking.total_price?.toLocaleString()}</p>
                                <p className="text-xs text-muted-foreground">
                                  Submitted {formatDateMonthDay(booking.created_at)}
                                </p>
                                <div className="flex gap-2 mt-3">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 hover:bg-red-50"
                                    onClick={() => openApprovalDialog(booking)}
                                  >
                                    <X className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-green-500 hover:bg-green-600"
                                    onClick={() => openApprovalDialog(booking)}
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    Approve
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onOpenChange={setApprovalDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Review Booking</DialogTitle>
            <DialogDescription>
              Booking #{selectedBooking?.booking_number} for {selectedBooking?.ambassador_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Client:</span>
                  <p className="font-medium">{selectedBooking?.client?.name || selectedBooking?.client?.email}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Tier:</span>
                  <p className="font-medium capitalize">{selectedBooking?.tier}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Total:</span>
                  <p className="font-medium">${selectedBooking?.total_price?.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Scope:</span>
                  <p className="font-medium capitalize">{selectedBooking?.usage_scope?.replace(/_/g, ' ')}</p>
                </div>
              </div>
            </div>
            <div>
              <Label>Admin Notes (Optional)</Label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes for this booking..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setApprovalDialog(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="text-red-600 hover:bg-red-50"
              onClick={() => handleApproveBooking(selectedBooking?.id, 'reject')}
              disabled={processing}
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4 mr-1" />}
              Reject
            </Button>
            <Button
              className="bg-green-500 hover:bg-green-600"
              onClick={() => handleApproveBooking(selectedBooking?.id, 'approve')}
              disabled={processing}
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAIBookings;
